import { useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Archive, Mail, Loader2, Users, CalendarClock, CheckCircle2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

export const SeasonPanel = ({ season, onChanged }) => {
  const [showInvite, setShowInvite] = useState(false);
  const [showRollover, setShowRollover] = useState(false);
  if (!season) return null;
  const opens = fmtDate(season.public_opens_at);
  return (
    <Card className="border-0 shadow-sm mb-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white" data-testid="season-panel">
      <CardContent className="py-5">
        <div className="grid md:grid-cols-[1fr,auto] gap-5 items-center">
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Season</p>
              <p className="text-2xl font-bold mt-1" data-testid="season-label">{season.rolled_over ? `${season.season} roster live` : `${season.archive_season} roster live`}</p>
              <p className="text-[11px] text-gray-400">{season.rolled_over ? `${season.archive_season} archived · ${season.live_attendees} on the ${season.season} roster` : `${season.live_attendees} attendees · ${season.confirmed_reservations} confirmed ${season.season} reservations waiting to convert`}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Alumni first shot</p>
              <p className="text-2xl font-bold mt-1 flex items-center gap-2" data-testid="alumni-access-label">
                {season.alumni_only_now ? <><CheckCircle2 className="w-5 h-5 text-emerald-400" />Alumni only</> : <><Users className="w-5 h-5 text-blue-300" />Open to all</>}
              </p>
              <p className="text-[11px] text-gray-400">{opens ? (season.alumni_only_now ? `Public opens ${opens}` : `Opened publicly ${opens}`) : 'No public opening date set (Tiers & Settings)'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">{season.archive_season} alumni</p>
              <p className="text-2xl font-bold mt-1" data-testid="alumni-count">{season.alumni_count}</p>
              <p className="text-[11px] text-gray-400">incl. cancelled · excl. waitlist</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={() => setShowInvite(true)} className="bg-[#d9a15b] text-[#1a0d10] hover:bg-[#f0d3a0]" data-testid="invite-alumni-btn">
              <Mail className="w-4 h-4 mr-2" />Invite {season.archive_season} alumni
            </Button>
            {!season.rolled_over && (
              <Button onClick={() => setShowRollover(true)} variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="start-season-btn">
                <Archive className="w-4 h-4 mr-2" />Start {season.season} season
              </Button>
            )}
          </div>
        </div>
      </CardContent>
      {showInvite && <InviteDialog season={season} onClose={() => setShowInvite(false)} onSent={onChanged} />}
      {showRollover && <RolloverDialog season={season} onClose={() => setShowRollover(false)} onDone={onChanged} />}
    </Card>
  );
};

const InviteDialog = ({ season, onClose, onSent }) => {
  const opens = fmtDate(season.public_opens_at);
  const [subject, setSubject] = useState(`You get first pick — ${season.season} ROJO Retreat beds are open to you now`);
  const [message, setMessage] = useState(
    `Thank you again for being part of the ${season.archive_season} retreat.\n\nAs a thank-you, you get first shot at ${season.season} beds before we open reservations to everyone. Three tiers this year: 200s Room $500, BO Room $400, Cabin $300. Pick your tier, and your bed is held 48 hours while your Venmo payment clears.${opens ? `\n\nReservations open to the public on ${opens}.` : ''}`
  );
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!window.confirm(`Send this to all ${season.alumni_count} ${season.archive_season} alumni?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reserve/admin/invite-alumni`, { subject, message }, { headers: getAuthHeaders() });
      toast.success(res.data.message); onSent?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); } finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl text-gray-900" data-testid="invite-alumni-dialog">
        <DialogHeader><DialogTitle>Invite {season.archive_season} alumni · {season.alumni_count} recipients</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!season.public_opens_at && (
            <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900"><AlertTriangle className="w-4 h-4 shrink-0" />No public opening date is set, so anyone can reserve right now. Set one in Tiers &amp; Settings first if you want alumni-only access.</div>
          )}
          <div><Label>Subject</Label><Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="invite-subject" /></div>
          <div><Label>Message</Label><Textarea rows={8} value={message} onChange={e => setMessage(e.target.value)} data-testid="invite-message" /></div>
          <p className="text-xs text-gray-500">The reserve link{opens ? ' and the public opening date' : ''} are appended automatically.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="bg-gradient-to-r from-wine to-burgundy text-white" data-testid="invite-send-btn">{sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Send invite</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RolloverDialog = ({ season, onClose, onDone }) => {
  const phrase = `ARCHIVE ${season.archive_season}`;
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reserve/admin/season/rollover`, { confirm: typed }, { headers: getAuthHeaders() });
      toast.success(`${season.archive_season} archived (${res.data.archived.attendees} men) · ${res.data.converted_reservations} reservations became ${season.season} attendees`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rollover failed'); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg text-gray-900" data-testid="rollover-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-wine" />Start the {season.season} season</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm text-gray-700">
          <p>This is a one-time switch. It will:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Snapshot all <strong>{season.live_attendees}</strong> {season.archive_season} attendees, rooms, assignments and payments into the read-only <strong>{season.archive_season} Archive</strong> (CSV export available).</li>
            <li>Clear the live roster and room board.</li>
            <li>Turn <strong>{season.confirmed_reservations}</strong> confirmed {season.season} reservations into attendees (paid in full at their tier price) — and do the same automatically for every future confirmation.</li>
          </ol>
          <p className="text-xs text-gray-500">Portal logins are kept — alumni who reserve again sign in with the same email. Email logs, forms, surveys and the ledger are untouched.</p>
          <div>
            <Label>Type <span className="font-mono font-bold">{phrase}</span> to confirm</Label>
            <Input value={typed} onChange={e => setTyped(e.target.value)} className="font-mono mt-1" data-testid="rollover-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={run} disabled={busy || typed.trim().toUpperCase() !== phrase} className="bg-red-600 hover:bg-red-700 text-white" data-testid="rollover-run-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}Archive &amp; start {season.season}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
